import { useTheme, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tokens } from '../../global/theme/tokens';
import Header from '../../components/special/Header';
import Templates from './Templates';
import TypographyEditor from '../../components/editor/TypographyEditor';
import ExoTabs from '../../components/exo/ExoTabs';
import { API_GET, DBRequest, useConfig } from '../../api/api';
import DBSettingTab from './DBSettingTab';
import TravelExpenseSettings from './TravelExpenseSettings';
import Feedback from '../../components/special/Feedback';
import AccessManager from './access-manager';
import { t } from 'i18next';
import { BankSettings } from './BankSettings';
import { hasPermissions, isAdmin } from '../../auth/hasPermissions';
import { NetworkStorage } from './NetworkStorage';
import { Files } from './Files';
import { LicensingManagement } from './LicensingManagement';
import { TemplateBlocksSettings } from './TemplateBlocksSettings';
import { AiUsageSettings } from './AiUsageSettings';
import HourlyRateAssignments from './HourlyRateAssignments';

const Settings = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleFeedback(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }
  const config = useConfig();

  // The tabs are built from this fetch, and with `noPrerender` a tab is
  // unmounted on switch and re-initialised from its `data` prop on return.
  // So after a save the config has to be fetched again, otherwise the tab
  // comes back with the values it was first opened with.
  function fetchConfigSettings() {
    if (!config) return;
    DBRequest({
      config,
      path: 'config',
      method: API_GET,
      onResponse: handleConfigSettings,
    });
  }

  useEffect(() => {
    fetchConfigSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const startTabs = [
    {
      key: 'licensing',
      label: t('Licensing'),
      content: <LicensingManagement onFeedback={handleFeedback} />,
      hidden: !hasPermissions('licensing-management', 'write'),
    },
    {
      key: 'ai-usage',
      label: t('AI Usage'),
      content: <AiUsageSettings onFeedback={handleFeedback} />,
      // Support/admin only: AI tiers are billed per seat, so regular users
      // and CEOs must not see or change them.
      hidden: !isAdmin(),
    },
    {
      key: 'storage',
      label: t('Storage'),
      content: <NetworkStorage onFeedback={handleFeedback} />,
      hidden: !hasPermissions('network-storage-setting', 'write'),
    },
    {
      key: 'file-types',
      label: t('File Types'),
      content: <Files onFeedback={handleFeedback} />,
      hidden: !hasPermissions('file-types-setting', 'write'),
    },
    {
      key: 'templates',
      label: t('Templates'),
      content: <Templates onFeedback={handleFeedback} />,
      hidden: !hasPermissions('template-setting', 'write'),
    },
    {
      key: 'template-blocks',
      label: t('Template Blocks'),
      content: <TemplateBlocksSettings />,
      hidden: !hasPermissions('template-setting', 'write'),
    },
    {
      key: 'typography',
      label: t('Typography'),
      content: <TypographyEditor onFeedback={handleFeedback} />,
      hidden: !hasPermissions('typography-setting', 'write'),
    },
    {
      key: 'bank-accounts',
      label: t('Bank Accounts'),
      content: <BankSettings onFeedback={handleFeedback} />,
      hidden: !hasPermissions('bank-account-setting', 'write'),
    },
    {
      key: 'access',
      label: t('Access'),
      content: <AccessManager />,
      hidden: !hasPermissions('access-setting', 'write'),
    },
    {
      key: 'hourly-rates',
      label: t('Calculative Hourly Rates'),
      content: <HourlyRateAssignments onFeedback={handleFeedback} />,
      hidden: !hasPermissions('user', 'write'),
    },
  ];

  const [tabs, setTabs] = useState(startTabs);

  // Allow deep-linking a tab via ?tab=<key> (e.g. the OAuth redirect back from
  // Dropbox / OneDrive returns to ?tab=storage). Falls back to the first
  // visible tab when the param is missing or unknown.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabKey = searchParams.get('tab');
  const requestedIndex = activeTabKey
    ? tabs.findIndex((tab) => tab.key === activeTabKey && !tab.hidden)
    : -1;
  const fallbackIndex = tabs.findIndex((tab) => !tab.hidden);
  const activeIndex =
    requestedIndex >= 0 ? requestedIndex : Math.max(fallbackIndex, 0);

  function handleTabChange(index) {
    const key = tabs[index]?.key;
    if (!key) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  }

  function handleConfigSettings(data) {
    const settings = data.data;

    var newTabs = [...startTabs];

    settings.forEach((setting) => {
      // The Travel Expense config category gets a dedicated tab with proper
      // labels, units and the statutory context instead of the generic editor.
      // project-setting keeps working as a fallback permission: the rates
      // lived in the Project category before they got their own.
      if (setting.category === 'travel expense') {
        newTabs.push({
          key: 'travel-expenses',
          label: t('Travel Expenses'),
          content: (
            <TravelExpenseSettings
              data={setting.items}
              onFeedback={handleFeedback}
              onSaved={fetchConfigSettings}
            />
          ),
          hidden: !(
            hasPermissions('travel-expense-setting', 'write') ||
            hasPermissions('project-setting', 'write')
          ),
        });
        return;
      }

      newTabs.push({
        key: setting.category,
        label: t(setting.label),
        content: (
          <DBSettingTab
            data={setting.items}
            onFeedback={handleFeedback}
            onResponse={fetchConfigSettings}
            api={setting.api}
          />
        ),
        hidden: !hasPermissions(`${setting.category}-setting`, 'write'),
      });
    });
    setTabs(newTabs);
  }

  return (
    <>
      <Header title={t('Settings')} subtitle="You can adjust settings here." />

      <ExoTabs
        tabs={tabs}
        orientation="vertical"
        noPrerender
        value={activeIndex}
        onChange={handleTabChange}
      />
      <Feedback state={alertState} setState={setAlertState} />
    </>
  );
};

export default Settings;
